import {
  EventAddOn,
  type EventDate,
  type EventDateType,
  type EventDescription,
} from '~/composables/types/event'
import { formatTime } from '~/utils/dateHelpers'
import { KftApiDefaultResponse } from '~/composables/types/kft-api'
import parseEventDateToCartItem from '~/utils/parseEventDateToCartItem'
import parseGiftcardToCartItem from '~/utils/parseGiftcardToCartItem'

export const useBooking = (type: EventDateType = 'PUBLIC') => {
  /** Refs & Variables
   * ---------------------------- */
  const selectedTime = ref<EventDate | undefined>(undefined)
  const selectedTicketQuantity = ref<number | undefined>(undefined)
  const selectedGiftcardQuantity = ref<number>(1)
  const selectedAddOn = ref<EventAddOn | undefined>(undefined)
  const availableTickets = ref<number[]>([])
  const route = useRoute()

  /** Methods
   * -------------------------- */

  /** @name prepareEventDateForBooking
   ** @desc Prepare an event date/time to put in the cart
   * @param event
   * @param isBookingAsPrivate Whether the date is being book as private or not (PUBLIC_PRIVATE can be)
   ** @param {EventDescription} event The event description */
  const prepareEventDateForBooking = (
    event: EventDescription,
    isBookingAsPrivate = false
  ) => {
    const queryId = route.query.qid?.toString() || undefined
    if (!selectedTime.value) {
      return undefined
    }

    return parseEventDateToCartItem(
      event,
      selectedTicketQuantity,
      selectedTime as Ref<EventDate>,
      selectedAddOn,
      isBookingAsPrivate,
      queryId,
    )
  }

  /** @name prepareGiftcardForBooking
   ** @desc Prepare a giftcard to put in the cart
   ** @param {Event} event The event description */
  const prepareGiftcardForBooking = (event: { data: EventDescription }) => {
    return parseGiftcardToCartItem(
      event.data,
      selectedTicketQuantity.value || selectedGiftcardQuantity.value,
      selectedTime as Ref<EventDate>
    )
  }

  /** @name setAvailableTickets
   ** @desc Sets the number of available tickets
   ** @param {Number} ticketNumber The amount of available tickets
   ** @param {Number} min The minimum amount of tickets for booking */
  const setAvailableTickets = (ticketNumber: number, min = 1) => {
    availableTickets.value = []

    for (let i = min; i <= Number(ticketNumber); i++) {
      availableTickets.value.push(i)
    }

    const availableTicketsAmount = availableTickets.value?.length || 0

    if (
      selectedTicketQuantity.value &&
      selectedTicketQuantity.value > availableTicketsAmount
    ) {
      selectedTicketQuantity.value = availableTicketsAmount
    } else {
      selectedTicketQuantity.value = availableTickets.value?.[0] || undefined
    }
  }

  /** @name onSelectTime
   ** @desc Triggered when the time is changed by the user. It sets the available tickets amount to select
   * @param {Object} time The event date/time object */
  const onSelectTime = (time: EventDate) => {
    if (!time) {
      return setAvailableTickets(0)
    }

    const qty =
      time.dateType === 'PUBLIC'
        ? time?.privateMaxTickets || time.availableTicketsQuantity
        : time.availableTicketsQuantity

    const minQty = 1

    setAvailableTickets(qty, minQty)
  }

  /**
   * @returns {boolean} true if it has the selected number of tickets available, false otherwise
   * */
  const updateAvailableTickets = (
    availableDates: KftApiDefaultResponse<EventDate[]>
  ): boolean => {
    if (!availableDates?.data?.length) {
      setAvailableTickets(0)
      return false
    }

    const currentSelectedEvent = availableDates.data.find(
      (item) => item.id === selectedTime.value?.id
    )

    if (!currentSelectedEvent) {
      setAvailableTickets(0)
      return false
    }

    if (
      availableTickets.value.length <
      currentSelectedEvent.availableTicketsQuantity
    ) {
      setAvailableTickets(currentSelectedEvent.availableTicketsQuantity)
    }

    return true
  }

  /** @name nextTimeAvailable
   ** @desc Get the next available time (not sold out and not closed)
   * @param {Object} availableDates The available date list */
  const nextTimeAvailable = (
    availableDates: EventDate[]
  ): EventDate | undefined => {
    return availableDates.find((item) => item.status === 'OPEN') || undefined
  }

  /**
   ** @name setNextTimeAvailable
   ** @description Sets the select time as the next available time option in the array
   ** @param {Array} availableDates - Array of available dates
   *  */
  const setNextTimeAvailable = (availableDates: EventDate[]) => {
    if (!availableDates?.length) {
      selectedTime.value = undefined
      return
    }

    /* set next time available as selected */
    selectedTime.value = nextTimeAvailable(availableDates)

    if (!selectedTime.value) {
      return
    }
    /* triggers select-time events (such as changing amount of available tickets in the select dropdown) */
    onSelectTime(selectedTime.value)

    // if (availableTickets.value.length >= 1) {
    //   selectedTicketQuantity.value = 1;
    // }
  }

  return {
    /* Refs */
    selectedTime,
    selectedTicketQuantity,
    selectedGiftcardQuantity,
    selectedAddOn,
    availableTickets,

    /* Methods */
    prepareEventDateForBooking,
    prepareGiftcardForBooking,
    setAvailableTickets,
    onSelectTime,
    updateAvailableTickets,
    setNextTimeAvailable,
  }
}
